'use client';

import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { LocationModalContent } from './location-modal-content.component';

import { useRecentLocationSearches } from '@/app/(main)/(pages)/(home)/_components/location/_hooks/use-recent-location-searches.hook';
import { useLocationSelection } from '@/app/(main)/(pages)/(home)/_components/location/_hooks/use-location-selection.hook';
import { useGoogleMaps } from '@/app/(main)/(pages)/(home)/_components/location/_hooks/use-google-maps.hook';
import { Modal } from '@/components/ui/modal.component';
import { authStore } from '@/store/auth.store';
import { modalsOpeningStore, setOpenLocationSelectionModal } from '@/store/modal';
import { resetUserLocation } from '@/store/user-location.store';
import useIsMobile from '@/hooks/use-is-mobile.hook';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Icon } from '@/components/ui/icon.component';
import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { AutocompletePrediction } from '@/types/google-maps.types';
import { useLoading } from '@/hooks/use-loading.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

const MIN_SEARCH_LENGTH = 3;

export const LocationSelectionModal = () => {
  const isMobile = useIsMobile();

  const analytics = getAnalyticsPlugin();
  const { getGoogleMapsPredictions } = useGoogleMaps();
  const { isLoading, startLoading, stopLoading } = useLoading();

  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const { recentSearches } = useRecentLocationSearches();
  const { open, deniedGeolocation } = useStore(modalsOpeningStore, (state) => ({
    open: state?.locationSelectionModalIsOpen,
    deniedGeolocation: state?.deniedGeolocation,
  }));

  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<AutocompletePrediction[]>([]);
  const [shouldShowEmptyResultsMessage, setShouldShowEmptyResultsMessage] = useState(false);

  const {
    handleMyLocation,
    handleSearchResultClick,
    handleSuggestionClick,
  } = useLocationSelection();

  useEffect(() => {
    async function handlePredictions() {
      startLoading();

      const predictions = await getGoogleMapsPredictions(searchValue);

      setShouldShowEmptyResultsMessage(!predictions?.length);
      setSearchResults(predictions);

      stopLoading();
    }

    if (searchValue.length >= MIN_SEARCH_LENGTH) {
      handlePredictions();
    } else {
      setShouldShowEmptyResultsMessage(false);
      setSearchResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  // Clear search when user logs in/out
  const prevIsLoggedInRef = useRef(isLoggedIn);

  useEffect(() => {
    if (prevIsLoggedInRef.current !== isLoggedIn) {
      prevIsLoggedInRef.current = isLoggedIn;
      setSearchDisplayValue('');
      setSearchResults([]);
      setSearchValue('');
      setShouldShowEmptyResultsMessage(false);
      resetUserLocation();
    }
  }, [isLoggedIn]);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;

    analytics.track(AnalyticsEnum.SEARCH_FOR_LOCATION, {
      termo: value,
    });

    setSearchDisplayValue(value);
    setSearchValue(value);
  };

  const resetSearchResults = () => {
    setSearchResults([]);
  };

  const handleOpenChange = (isOpen: boolean): void => {
    setOpenLocationSelectionModal(isOpen);
  };

  const sharedContentProps = {
    searchDisplayValue,
    searchResults,
    isLoading,
    shouldShowEmptyResultsMessage,
    deniedGeolocation,
    isLoggedIn,
    recentSearchesCount: recentSearches.length,
    onSearchInput: handleSearchInput,
    onUseMyLocation: handleMyLocation,
    onLocationClick: handleSearchResultClick,
    onSuggestionClick: handleSuggestionClick,
    onResetSearchResults: resetSearchResults,
  };

  return (
    <>
      {open && <TrackUserView eventName={AnalyticsEnum.VIEW_LOCATION_MODAL} />}

      {isMobile
        ? (
            <Sheet open={open} onOpenChange={handleOpenChange}>
              <SheetContent
                side="bottom"
                closingTagPosition="right"
                className="w-full p-0 h-full"
                hasHeader={true}
              >
                <SheetHeader>
                  <div className="flex justify-start gap-[10px] p-6 px-5 pb-0 items-start">
                    <SheetTitle>
                      <div className="flex gap-2">
                        <Icon
                          icon={faLocationDot}
                          size="xl"
                          className="text-green-400 mt-1"
                        />
                        <Typography variant={TypographyVariant.MODAL_TITLE}>
                          Qual cidade será o atendimento?
                        </Typography>
                      </div>
                    </SheetTitle>
                    <SheetClose
                      asChild
                      className="text-gray-500 cursor-pointer px-2 hover:bg-gray-50"
                    >
                      <Icon
                        data-testid={DataTestIdEnum.LOCATION_SELECTION_MODAL_CLOSE_BUTTON}
                        icon={faTimes}
                        size="xl"
                        className="hover:text-gray-900 mt-1"
                      />
                    </SheetClose>
                  </div>
                </SheetHeader>
                <LocationModalContent {...sharedContentProps} />
              </SheetContent>
            </Sheet>
          )
        : (
            <Modal open={open} onOpenChange={handleOpenChange}>
              <Modal.Content
                title="Qual cidade será o atendimento?"
                className="bg-white md:max-w-[50rem] md:min-h-[32rem]"
                icon={faLocationDot}
                iconStyle="text-green-400"
              >
                <LocationModalContent {...sharedContentProps} />
              </Modal.Content>
            </Modal>
          )}
    </>
  );
};
